import { useEffect, useState } from "react";
import { Button, Collapsible, inputCls, Json, PageHeader } from "../../components/ui";
import { List, listRowCls } from "../../components/List";
import { TextLink } from "../../components/TextLink";
import { get } from "../../lib/api";
import { realtime } from "../../lib/ws";

type AuditRow = {
  id: string; ts: string; actorType: string; actorId: string | null; action: string; entityType: string | null;
  entityId: string | null; conversationId: string | null; runId: string | null; data: unknown; ip: string | null;
};

const FILTERS = ["action", "actorType", "entityType", "entityId", "conversationId", "runId"] as const;
type Filters = Record<(typeof FILTERS)[number], string>;

export function AdminAudit() {
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [filters, setFilters] = useState<Filters>({ action: "", actorType: "", entityType: "", entityId: "", conversationId: "", runId: "" });
  const [hasMore, setHasMore] = useState(false);

  const qs = (extra = "") => {
    const p = new URLSearchParams();
    for (const k of FILTERS) if (filters[k]) p.set(k, filters[k]);
    return `/api/audit?limit=200&${p.toString()}${extra}`;
  };

  const matches = (r: AuditRow) =>
    (!filters.action || r.action.startsWith(filters.action)) &&
    (!filters.actorType || r.actorType === filters.actorType) &&
    (!filters.entityType || r.entityType === filters.entityType) &&
    (!filters.entityId || r.entityId === filters.entityId) &&
    (!filters.conversationId || r.conversationId === filters.conversationId) &&
    (!filters.runId || r.runId === filters.runId);

  useEffect(() => {
    get<AuditRow[]>(qs()).then((r) => {
      setRows(r);
      setHasMore(r.length === 200);
    }, () => {});
    return realtime.subscribe("audit", (e) => {
      const row = e.payload as AuditRow;
      if (matches(row)) setRows((cur) => (cur.some((x) => x.id === row.id) ? cur : [row, ...cur]));
    });
  }, [JSON.stringify(filters)]);

  return (
    <div>
      <PageHeader title="Audit log" subtitle="Append-only record of every action by users, agents and the system. Live." />
      <div className="grid grid-cols-1 gap-2 px-4 pt-4 sm:flex sm:flex-wrap md:px-6">
        {FILTERS.map((k) => (
          <input
            key={k}
            className={`${inputCls} sm:w-44`}
            placeholder={k === "action" ? "action prefix (e.g. run.)" : k}
            value={filters[k]}
            onChange={(e) => setFilters({ ...filters, [k]: e.target.value.trim() })}
          />
        ))}
      </div>
      <div className="space-y-3 px-4 py-4 md:px-6">
        <List empty="No matching entries.">
          {rows.map((r) => (
            <div key={r.id} className={listRowCls}>
              <Collapsible
                summary={
                  <span className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-0.5 font-mono text-xs md:flex-nowrap">
                    <span className="shrink-0 text-faint md:w-44">{new Date(r.ts).toLocaleString()}</span>
                    <span className="shrink-0 font-medium md:w-52">{r.action}</span>
                    <span className="shrink-0 truncate text-muted md:w-40">
                      {r.actorType}
                      {r.actorId ? `:${r.actorId.slice(0, 8)}` : ""}
                    </span>
                    <span className="truncate text-muted">
                      {r.entityType ? `${r.entityType}:${r.entityId?.slice(0, 8)}` : ""}
                      {r.ip ? ` · ${r.ip}` : ""}
                    </span>
                  </span>
                }
              >
                <div className="space-y-1">
                  <div className="flex gap-3 text-xs">
                    {r.runId && <TextLink to={`/admin/runs/${r.runId}`}>run</TextLink>}
                    {r.conversationId && <TextLink to={`/admin/conversations/${r.conversationId}`}>conversation</TextLink>}
                  </div>
                  <Json value={r.data} className="max-h-96" />
                </div>
              </Collapsible>
            </div>
          ))}
        </List>
        {hasMore && (
          <Button
            onClick={() =>
              get<AuditRow[]>(qs(`&before=${rows.at(-1)!.id}`)).then((more) => {
                setRows((cur) => [...cur, ...more]);
                setHasMore(more.length === 200);
              })
            }
          >
            Load more
          </Button>
        )}
      </div>
    </div>
  );
}
